import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import type { ComponentPropsWithRef, ReactElement } from "react";
import React from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

import { Spinner2 } from "./Spinner";

export type ButtonProps = {
  classNameChildren?: string;
  icon?: ReactElement;
  isLoading?: boolean;
  labelLoading?: string;
  rawChildren?: boolean;
  testId?: string;
} & AriaButtonProps &
  ComponentPropsWithRef<"button"> &
  VariantProps<typeof buttonStyles>;

export function Button2({
  children,
  className,
  classNameChildren,
  icon: Icon,
  isLoading = false,
  labelLoading = "Please wait...",
  rawChildren,
  size,
  variant,
  ...buttonProps
}: ButtonProps) {
  const {
    body,
    content,
    icon: iconStyle,
    loadingText,
    overlayText,
    spinner,
  } = buttonStyles({
    hasIcon: !!Icon,
    isLoading,
    size,
    variant,
  });

  return (
    <AriaButton
      aria-busy={isLoading}
      aria-disabled={isLoading}
      className={cn(body(), "atom-focus", className)}
      data-public
      {...buttonProps}
    >
      {rawChildren && children}

      {!rawChildren && (
        <>
          <span
            aria-hidden={!isLoading}
            aria-live="polite"
            className={cn(overlayText(), loadingText())}
          >
            <Spinner2 className={spinner()} />
            {labelLoading}
          </span>

          <div aria-hidden={isLoading} className={cn(content(), classNameChildren)}>
            {Icon &&
              React.cloneElement(Icon, {
                className: cn(iconStyle(), Icon.props.className),
              })}

            <span className="trim-both tw:empty:hidden">{children}</span>
          </div>
        </>
      )}
    </AriaButton>
  );
}

export const buttonStyles = tv({
  defaultVariants: {
    isLoading: false,
    size: "md",
    variant: "primary",
  },
  slots: {
    body: cn(
      "tw:relative tw:inline-flex tw:items-center tw:justify-center",
      "tw:outline-none",
      "tw:border-px tw:rounded! tw:border tw:border-solid",
      "tw:cursor-pointer tw:whitespace-nowrap",
      "ease tw:transition-all tw:duration-150",
    ),
    content: "tw:transition-opacity tw:duration-300 trim-both",
    icon: "tw:shrink-0",
    loadingText: "",
    overlayText: cn(
      "tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:transition-opacity tw:duration-300",
      "tw:pointer-events-none", // important for the Cypress test-runner to properly click the buttons
    ),
    spinner: "tw:mr-3 tw:w-5",
  },
  variants: {
    hasIcon: {
      true: {
        content: "tw:flex tw:items-center tw:gap-1.5",
      },
    },
    isLoading: {
      false: {
        content: "tw:opacity-100",
        loadingText: "tw:opacity-0",
      },
      true: {
        body: "tw:pointer-events-none",
        content: "tw:opacity-0",
        loadingText: "tw:opacity-100",
      },
    },
    size: {
      lg: {
        body: "tw:h-12 tw:py-3 tw:px-4 tw:text-md!",
        icon: "tw:size-6",
        overlayText: "tw:leading-12",
      },
      md: {
        body: "tw:h-11 tw:py-2.5 tw:px-4 tw:text-md!",
        icon: "tw:size-5",
        overlayText: "tw:leading-11",
      },
      sm: {
        body: "tw:h-9 tw:py-2 tw:px-2.5 tw:text-sm!",
        icon: "tw:size-5",
        overlayText: "tw:leading-9",
        spinner: "tw:w-4 tw:mr-2",
      },
      xl: {
        body: "tw:h-14 tw:py-4 tw:px-6 tw:text-md!",
        icon: "tw:size-6",
        overlayText: "tw:leading-14",
      },
      xs: {
        body: "tw:h-7 tw:py-1 tw:px-3 tw:text-xs!",
        icon: "tw:size-4",
        overlayText: "",
      },
    },
    variant: {
      blank: {
        body: "tw:p-0 tw:border-transparent tw:h-auto tw:disabled:opacity-40 tw:disabled:cursor-not-allowed",
      },
      "destructive-primary": {
        // temporary, awaiting Konrad's input
        body: cn(
          "tw:font-bold",
          "tw:border-stroke-error-primary tw:bg-transparent tw:text-text-error-primary",
          "tw:hover:border-stroke-error-secondary tw:hover:text-text-error-secondary",
          "tw:active:border-fill-error-primary tw:active:text-text-error-primary",
          "tw:disabled:cursor-not-allowed tw:disabled:border-fill-quinary tw:disabled:text-text-quaternary",
        ),
      },
      "destructive-secondary": {
        body: cn(
          "tw:font-bold",
          "tw:bg-fill-error-primary tw:text-text-white!",
          "tw:hover:bg-fill-error-secondary",
          "tw:active:bg-fill-error-primary-w20",
          "tw:disabled:cursor-not-allowed tw:disabled:bg-fill-quaternary",
        ),
      },
      link: {
        body: cn(
          "tw:font-bold",
          "tw:h-auto tw:p-0",
          "tw:border-transparent tw:bg-transparent tw:text-text-teal-primary",
          "tw:hover:text-text-teal-secondary",
          "tw:active:text-text-teal-primary-w20",
          "tw:disabled:cursor-not-allowed tw:disabled:border-transparent tw:disabled:text-text-quaternary",
        ),
      },
      primary: {
        body: cn(
          "tw:font-bold",
          "tw:bg-fill-teal-primary tw:text-text-white!",
          "tw:border-fill-teal-primary",
          "tw:hover:text-white tw:hover:border-fill-teal-secondary tw:hover:bg-fill-teal-secondary",
          "tw:active:border-fill-teal-primary-w20 tw:active:bg-fill-teal-primary-w20",
          "tw:disabled:cursor-not-allowed tw:disabled:border-fill-quaternary tw:disabled:bg-fill-quaternary",
        ),
      },
      secondary: {
        body: cn(
          "tw:font-bold",
          "tw:border-fill-teal-primary tw:bg-fill-white tw:text-text-teal-primary",
          "tw:hover:border-stroke-teal-secondary tw:hover:text-text-teal-secondary",
          "tw:active:border-fill-teal-primary-w20 tw:active:text-fill-teal-primary-w20",
          "tw:disabled:cursor-not-allowed tw:disabled:border-fill-quinary tw:disabled:text-text-quaternary",
        ),
      },
      text: {
        body: cn(
          "tw:h-auto tw:p-0",
          "tw:border-transparent tw:bg-transparent tw:text-text-teal-primary",
          "tw:hover:text-text-teal-secondary",
          "tw:active:text-text-teal-primary-w20",
          "tw:disabled:cursor-not-allowed tw:disabled:border-transparent tw:disabled:text-text-quaternary",
        ),
      },
    },
  },
});
